/**
 * الرئيسية.
 *
 * قاعدة مثبّتة: Action first → Market context second. الرئيسية ليست Dashboard.
 *
 * الاتجاه البصري (لغة المخطط المساحي):
 *   · شبكة خطوط شعرية خلف البطل — يبدو المنتج مرسوماً على ورق مسّاح.
 *   · الأرقام بخط أحادي المسافة فتصطف كجدول قياس.
 *   · حقل واحد هو البطل، لا بطاقات مبعثرة.
 *   · التركوازي ثلاث مرات فقط: الشعار · زر الفعل · الرقم الإيجابي.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { color } from '@/design/tokens';
import { SectionTitle } from '@/components/primitives/Primitives';
import { AlertFeed } from '@/components/data/Market';
import { LoadingState, ErrorState } from '@/components/states/States';
import { UnavailablePanel } from '@/components/states/Unavailable';
import { MapSurface, type MapSurfaceMessage } from '@/components/map/MapSurface';
import { useCities, useDistrictSearch, useMarketPulse } from '@/services/market';
import type { MarketPulseRow } from '@/domain/types';

/**
 * يفصل الرقم عن وحدته ليُعرض الرقم كبيراً والوحدة أصغر.
 * القياس يُقرأ أسرع حين تكون الوحدة ثانوية بصرياً.
 */
function splitUnit(value: string): { figure: string; unit: string } {
  const match = /^([+\u2212-]?[\d.,]+)\s*(.*)$/u.exec(value.trim());
  if (!match) return { figure: value, unit: '' };
  return { figure: match[1]!, unit: match[2]!.trim() };
}

function toneColor(tone: MarketPulseRow['tone']): string {
  if (tone === 'up') return color.teal;
  if (tone === 'down' || tone === 'warn') return color.amberText;
  return color.text;
}

/** قياس واحد في شريط الأرقام — الرقم بطل والوحدة تابعة. */
function Measure({ row }: { row: MarketPulseRow }): JSX.Element {
  const { figure, unit } = splitUnit(row.value);
  return (
    <div>
      <div className="nq-num" dir="ltr" style={{ fontSize: 21, color: toneColor(row.tone) }}>
        {figure}
        {unit ? <span style={{ fontSize: 12, marginInlineStart: 3 }}>{unit}</span> : null}
      </div>
      <div style={{ fontSize: 10.5, color: color.micro, marginTop: 4 }}>{row.label}</div>
    </div>
  );
}

export default function HomeRoute(): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const suggestions = useDistrictSearch(query);

  /**
   * المدينة قابلة للاختيار — لا مثبّتة في الكود.
   *
   * الأرقام تختلف جوهرياً بين المدن، وعرض رقم مدينة واحدة بلا تسميتها يجعل
   * المستخدم يظنه رقم المملكة كلها. المدن تأتي من الخادم فلا تحتاج تعديل
   * كود عند إضافة مدينة جديدة.
   */
  const cities = useCities();
  const available = cities.data ?? [];
  const [city, setCity] = useState<string | null>(null);
  /**
   * الرياض هي الافتراض، لا أول المدن أبجدياً.
   *
   * `available[0]` كان يعطي «الخبر» — أصغر المدن تغطيةً وأولها أبجدياً —
   * بينما الخريطة أسفل الشاشة نفسها تفتح على الرياض. فتعرض شاشة واحدة
   * مؤشرات مدينة وخريطة مدينة أخرى. والرياض أكبر سوق في البيانات
   * (١٥٬٨٦٦ صفقة)، فهي الافتراض الذي يخدم أكثر الزوّار.
   */
  const activeCity = city ?? (available.includes('الرياض') ? 'الرياض' : (available[0] ?? 'الرياض'));
  const pulse = useMarketPulse(activeCity);

  function onMapMessage(m: MapSurfaceMessage): void {
    if (m.type === 'open-district' && m.name && m.city) {
      navigate(`/district/${encodeURIComponent(`${m.city}|${m.name}`)}`);
    }
  }

  const rows = pulse.data?.rows ?? [];
  const headline = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="nq-enter">
      {/* ------------------------------------------------------------- البطل */}
      <section
        className="nq-grid"
        style={{
          margin: '0 -28px',
          padding: '48px 28px 32px',
          borderBottom: `1px solid ${color.hairlineSoft}`,
        }}
      >
        <div style={{ maxWidth: 720 }}>
          <h1
            style={{
              fontSize: 34,
              fontWeight: 500,
              lineHeight: 1.35,
              letterSpacing: '-.01em',
              margin: 0,
            }}
          >
            أين يقف السعر
            <br />
            <span style={{ color: color.textMuted }}>من السوق فعلاً؟</span>
          </h1>

          <p
            style={{
              marginTop: 12,
              marginBottom: 0,
              fontSize: 13.5,
              lineHeight: 1.9,
              color: color.textSubtle,
              maxWidth: 430,
            }}
          >
            اكتب الحي والمساحة والسعر المطلوب. نقارنه بالصفقات المسجّلة رسمياً، ونقول لك ما
            تثبته البيانات — لا أكثر.
          </p>

          <div style={{ position: 'relative', marginTop: 26, maxWidth: 560 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                className="nq-input"
                style={{ flex: '1 1 220px', minWidth: 0, padding: '14px 16px', fontSize: 14 }}
                placeholder="ابحث عن حي…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="ابحث عن حي"
              />
              <button
                type="button"
                className="nq-btn nq-btn--primary"
                style={{ padding: '14px 26px', fontSize: 13.5, whiteSpace: 'nowrap' }}
                onClick={() => navigate('/evaluate')}
              >
                قيّم أرض
              </button>
            </div>

            {query.trim() && suggestions.length > 0 ? (
              <div
                role="listbox"
                aria-label="أحياء مقترحة"
                style={{
                  position: 'absolute',
                  insetInlineStart: 0,
                  insetInlineEnd: 122,
                  marginTop: 6,
                  background: color.panel600,
                  border: `1px solid ${color.hairline}`,
                  borderRadius: 12,
                  overflow: 'hidden',
                  zIndex: 30,
                }}
              >
                {suggestions.slice(0, 5).map((s) => (
                  <button
                    key={s.districtId}
                    type="button"
                    role="option"
                    aria-selected="false"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      width: '100%',
                      padding: '11px 14px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: `1px solid ${color.hairlineSoft}`,
                      color: color.text,
                      fontSize: 13,
                      cursor: 'pointer',
                      textAlign: 'start',
                    }}
                    onClick={() => navigate(`/district/${encodeURIComponent(s.districtId)}`)}
                  >
                    <span>
                      {s.name} <span style={{ color: color.micro }}>· {s.city}</span>
                    </span>
                    {s.valuationAvailable === false ? (
                      <span
                        data-testid="district-coverage-hint"
                        style={{
                          fontSize: 10.5,
                          color: color.micro,
                          border: `1px solid ${color.hairline}`,
                          borderRadius: 999,
                          padding: '2px 8px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        بيانات محدودة
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* مبدّل المدينة — الأرقام تحته تتبعه */}
          {available.length > 1 ? (
            <div
              data-testid="city-switcher"
              role="tablist"
              aria-label="اختر المدينة"
              style={{
                display: 'flex',
                gap: 6,
                marginTop: 30,
                flexWrap: 'wrap',
              }}
            >
              {available.map((c) => {
                const active = c === activeCity;
                return (
                  <button
                    key={c}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setCity(c)}
                    style={{
                      background: active ? 'rgba(0,229,199,.10)' : 'transparent',
                      border: `1px solid ${active ? 'rgba(0,229,199,.34)' : color.hairline}`,
                      borderRadius: 999,
                      color: active ? color.teal : color.textSubtle,
                      fontSize: 12,
                      fontWeight: active ? 500 : 400,
                      padding: '6px 15px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          ) : null}

          {/* ثلاثة أرقام تثبت الجدية — لا أكثر */}
          {headline.length > 0 ? (
            <div
              style={{
                display: 'flex',
                gap: 26,
                marginTop: available.length > 1 ? 20 : 32,
                paddingTop: 22,
                borderTop: `1px solid ${color.hairlineSoft}`,
                flexWrap: 'wrap',
              }}
            >
              {headline.map((r, i) => (
                <div key={r.label} style={{ display: 'flex', gap: 26 }}>
                  {i > 0 ? (
                    <div
                      aria-hidden
                      style={{ width: 1, background: color.hairlineSoft, alignSelf: 'stretch' }}
                    />
                  ) : null}
                  <Measure row={r} />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* -------------------------------------------------------- سياق السوق */}
      <div className="nq-row" style={{ marginTop: 34, alignItems: 'stretch' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <SectionTitle
            title="نبض السوق"
            action={<span style={{ fontSize: 10.5, color: color.micro }}>{activeCity}</span>}
          />
          {pulse.status === 'loading' ? (
            <LoadingState message="نقرأ مؤشرات السوق..." blocks={2} />
          ) : pulse.status === 'unavailable' ? (
            <UnavailablePanel reason="coming-soon" title="نبض السوق" />
          ) : pulse.status === 'error' ? (
            <ErrorState message="تعذّر تحميل نبض السوق." onRetry={pulse.reload} />
          ) : rest.length > 0 ? (
            <div>
              {rest.map((r) => {
                const { figure, unit } = splitUnit(r.value);
                return (
                  <div
                    key={r.label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '11px 0',
                      borderBottom: `1px solid ${color.hairlineSoft}`,
                    }}
                  >
                    <span style={{ fontSize: 13, color: color.textSubtle }}>{r.label}</span>
                    <span
                      className="nq-num"
                      dir="ltr"
                      style={{ fontSize: 13.5, color: toneColor(r.tone) }}
                    >
                      {figure}
                      {unit ? (
                        <span style={{ fontSize: 11, marginInlineStart: 3, opacity: 0.7 }}>
                          {unit}
                        </span>
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ fontSize: 12.5, color: color.micro, lineHeight: 1.8 }}>
              المؤشرات الأساسية معروضة أعلاه.
            </p>
          )}
        </div>

        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <SectionTitle title="آخر التنبيهات" />
          {pulse.status === 'loading' ? (
            <LoadingState blocks={2} />
          ) : pulse.data && pulse.data.alerts.length > 0 ? (
            <AlertFeed alerts={pulse.data.alerts} />
          ) : (
            <UnavailablePanel
              reason="coming-soon"
              title="التنبيهات"
              detail="التنبيهات تحتاج مراقبة تغيّرات عبر الزمن — تبدأ بعد تراكم عدة دورات استيراد."
            />
          )}
        </div>
      </div>

      {/* ----------------------------------------------------------- الخريطة */}
      <section style={{ marginTop: 34 }}>
        <SectionTitle
          title="الخريطة"
          action={
            <button type="button" className="nq-btn nq-btn--ghost" onClick={() => navigate('/map')}>
              افتح الخريطة الكاملة
            </button>
          }
        />
        <MapSurface city={activeCity} mode="opportunity" height={320} onMessage={onMapMessage} />
      </section>
    </div>
  );
}
